import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ExternalLink } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CATEGORIES, getProjectsByCategory } from "../data/projects";

const Portfolio = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeCategory, setActiveCategory] = React.useState("Tous");
  const [missingImages, setMissingImages] = React.useState({});

  const filteredProjects = getProjectsByCategory(activeCategory);

  return (
    <div className="bg-gray-950 text-white min-h-screen font-sans">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6 animate-slideUp">
              Portfolio
            </div>
            <h1 className="font-display text-5xl md:text-7xl text-white mb-8 animate-slideUp" style={{ animationDelay: "0.1s" }}>
              Nos <span className="text-flowkraft-secondary italic">réalisations</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed animate-slideUp" style={{ animationDelay: "0.2s" }}>
              Découvrez comment nous aidons nos clients à transformer leurs idées en produits digitaux exceptionnels.
            </p>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-flowkraft-primary/5 to-transparent pointer-events-none" />
      </section>

      {/* Filter Section */}
      <section className="py-12 border-y border-white/5 bg-gray-900/20">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap gap-4 items-center">
            <span className="text-sm text-gray-500 uppercase tracking-widest mr-4">Filtrer par :</span>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 border transition-all duration-300 text-sm font-medium ${
                  activeCategory === cat
                    ? "bg-flowkraft-secondary border-flowkraft-secondary text-gray-950"
                    : "border-white/10 text-gray-400 hover:border-white/30"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div 
                key={project.title} 
                className="group flex flex-col bg-gray-900/30 border border-white/5 hover:border-flowkraft-primary/30 transition-all duration-500 overflow-hidden"
              >
                {/* Project Image */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  {/* Placeholder affiché tant que la capture n'est pas déposée */}
                  {missingImages[project.title] ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-gray-600">
                      <span className="font-display text-3xl">{project.title}</span>
                      <span className="text-[10px] uppercase tracking-widest mt-2">Visuel à venir</span>
                    </div>
                  ) : (
                    <img
                      src={project.image}
                      alt={project.title}
                      onError={() =>
                        setMissingImages((prev) => ({ ...prev, [project.title]: true }))
                      }
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-gray-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                    {project.link && (
                      <a 
                        href={project.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-white text-gray-950 rounded-full flex items-center justify-center transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-gray-950/80 backdrop-blur-sm border border-white/10 text-[10px] uppercase tracking-widest text-flowkraft-secondary">
                      {project.category}
                    </span>
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-8 flex flex-col flex-grow">
                  <h3 className="font-display text-2xl text-white mb-3 group-hover:text-flowkraft-secondary transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-grow">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.map(tag => (
                      <span key={tag} className="text-[10px] text-gray-500 border border-white/5 px-2 py-0.5 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {project.link ? (
                    <a 
                      href={project.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-semibold text-white group-hover:text-flowkraft-secondary transition-colors"
                    >
                      Voir le projet <ArrowUpRight className="ml-2 w-4 h-4" />
                    </a>
                  ) : (
                    <span className="text-sm font-semibold text-gray-600 cursor-default">
                      Cas d'étude privé
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900/50 relative overflow-hidden">
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl text-white mb-8">
            Votre projet pourrait être le prochain
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-12">
            Nous sommes prêts à relever de nouveaux défis. Discutons de la manière dont nous pouvons donner vie à votre vision.
          </p>
          <Link to="/#contact" className="btn-solid inline-flex items-center gap-2">
            Démarrer un projet
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        {/* Decorative Grid */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
             style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      </section>

      <Footer />
    </div>
  );
};

export default Portfolio;
